/**
 * Друковані форми документів ОЗ (2026-08-29, рішення власника — «редактор
 * друку для всіх документів пункту меню»): акт введення в експлуатацію,
 * акт списання, акт приймання-передачі (продаж). Канон .print-page +
 * PrintActions (друк / підписати і зберегти у вкладення).
 */
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { assetCommissioning, assetWriteOff } from '@/api/assets';
import { sale as saleApi, type SaleItem } from '@/api/asset_inventory_sale';
import { assetsRegistry } from '@/api/assetsRegistry';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (v: string | null | undefined) => v ? new Date(v).toLocaleDateString('uk-UA') : '';
const cell = { border: '1px solid #000', padding: '2px 6px' } as const;
const head = { ...cell, background: '#f0f0f0' } as const;

function usePrintMode() {
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);
}
function useAssetNames(orgId: string | undefined) {
  const q = useQuery({
    queryKey: ['assets-registry-all', orgId],
    queryFn: () => assetsRegistry.list({ organization_id: orgId!, limit: 500 }),
    enabled: !!orgId,
  });
  return useMemo(() => {
    const m = new Map<string, { name: string; inv: string }>();
    q.data?.items.forEach((a) => m.set(a.id, { name: a.name, inv: a.inventory_number }));
    return m;
  }, [q.data]);
}
function Shell({ org, title, number, date, docMeta, children }: {
  org: { name: string }; title: string; number: string; date: string;
  docMeta: { organizationId: string; documentType: string; documentId: string; filename: string };
  children: React.ReactNode;
}) {
  return (
    <div className="print-page" style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)' }}>
      <PrintActions docMeta={{ ...docMeta, orgName: org.name }} />
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: '9pt' }}>{org.name}</div>
        <h2 style={{ margin: '4px 0' }}>{title} № {number}</h2>
        <div>від {fmtD(date)}</div>
      </div>
      {children}
    </div>
  );
}
function Signatures({ left, right }: { left: string; right: string }) {
  return (
    <div style={{ marginTop: 28, display: 'flex', justifyContent: 'space-between' }}>
      <div>{left}: ____________________</div>
      <div>{right}: ____________________</div>
    </div>
  );
}

export function AssetCommissioningPrintPage() {
  const { id } = useParams<{ id: string }>();
  usePrintMode();
  const docQ = useQuery({ queryKey: ['asset-commissioning', id], queryFn: () => assetCommissioning.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const names = useAssetNames(d?.organization_id);
  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const total = d.items.reduce((s, it) => s + Number(it.primary_cost_snapshot), 0);
  return (
    <Shell org={org} title="Акт введення в експлуатацію основних засобів" number={d.document_number} date={d.document_date}
      docMeta={{ organizationId: d.organization_id, documentType: 'doc.asset_commissioning', documentId: d.id, filename: `Введення в експлуатацію ${d.document_number}` }}>
      <p>Стан після введення: <b>{d.target_status === 'PARKED' ? 'на зберіганні' : 'в експлуатації'}</b>{d.comment ? `. ${d.comment}` : ''}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
        <thead><tr>{['№', 'Інв. №', 'Найменування', 'Первісна вартість', 'Строк корисного використання, міс'].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
        <tbody>
          {d.items.map((it) => {
            const a = names.get(it.fixed_asset_id ?? it.intangible_asset_id ?? it.low_value_asset_id ?? '');
            return (
              <tr key={it.id ?? it.line_number}>
                <td style={cell}>{it.line_number}</td><td style={cell}>{a?.inv ?? ''}</td><td style={cell}>{a?.name ?? '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.primary_cost_snapshot)}</td>
                <td style={{ ...cell, textAlign: 'center' }}>{it.useful_life_months_snapshot ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr style={{ fontWeight: 'bold' }}><td colSpan={3} style={cell}>Разом</td><td style={{ ...cell, textAlign: 'right' }}>{fmt2(total)}</td><td style={cell} /></tr></tfoot>
      </table>
      <Signatures left="Голова комісії" right="МВО" />
    </Shell>
  );
}

const REASON_UA: Record<string, string> = { OBSOLETE: 'зношення / моральне старіння', BROKEN: 'поломка', LOST: 'втрата / нестача', OTHER: 'інше' };

export function AssetWriteOffPrintPage() {
  const { id } = useParams<{ id: string }>();
  usePrintMode();
  const docQ = useQuery({ queryKey: ['asset-write-off', id], queryFn: () => assetWriteOff.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const names = useAssetNames(d?.organization_id);
  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const sum = (f: 'primary_cost_snapshot' | 'accumulated_depreciation_snapshot') => d.items.reduce((s, it) => s + Number(it[f] ?? 0), 0);
  return (
    <Shell org={org} title="Акт списання основних засобів" number={d.document_number} date={d.document_date}
      docMeta={{ organizationId: d.organization_id, documentType: 'doc.asset_write_off', documentId: d.id, filename: `Списання ОЗ ${d.document_number}` }}>
      <p>Причина списання: <b>{REASON_UA[d.reason] ?? d.reason}</b>{d.reason_details ? ` — ${d.reason_details}` : ''}{d.comment ? `. ${d.comment}` : ''}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
        <thead><tr>{['№', 'Інв. №', 'Найменування', 'Первісна вартість', 'Знос', 'Залишкова вартість'].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
        <tbody>
          {d.items.map((it) => {
            const a = names.get(it.fixed_asset_id ?? it.intangible_asset_id ?? it.low_value_asset_id ?? '');
            const acc = Number(it.accumulated_depreciation_snapshot ?? 0);
            return (
              <tr key={it.id ?? it.line_number}>
                <td style={cell}>{it.line_number}</td><td style={cell}>{a?.inv ?? ''}</td><td style={cell}>{a?.name ?? '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.primary_cost_snapshot)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(acc)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(Number(it.primary_cost_snapshot) - acc)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr style={{ fontWeight: 'bold' }}><td colSpan={3} style={cell}>Разом</td>
          <td style={{ ...cell, textAlign: 'right' }}>{fmt2(sum('primary_cost_snapshot'))}</td>
          <td style={{ ...cell, textAlign: 'right' }}>{fmt2(sum('accumulated_depreciation_snapshot'))}</td>
          <td style={{ ...cell, textAlign: 'right' }}>{fmt2(sum('primary_cost_snapshot') - sum('accumulated_depreciation_snapshot'))}</td></tr></tfoot>
      </table>
      <Signatures left="Голова комісії" right="МВО" />
    </Shell>
  );
}

export function FixedAssetSalePrintPage() {
  const { id } = useParams<{ id: string }>();
  usePrintMode();
  const docQ = useQuery({ queryKey: ['fa-sale', id], queryFn: () => saleApi.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const names = useAssetNames(d?.organization_id);
  const contractorsQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll(), enabled: !!d?.counterparty_id });
  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const buyer = d.counterparty_id ? (contractorsQ.data?.items.find((c) => c.id === d.counterparty_id)?.name ?? '…')
    : `${d.external_buyer_name ?? ''}${d.external_buyer_tax_id ? ` (${d.external_buyer_tax_id})` : ''}`;
  const sum = (f: (it: SaleItem) => number) => d.items.reduce((s, it) => s + f(it), 0);
  return (
    <Shell org={org} title="Акт приймання-передачі (продажу) основних засобів" number={d.document_number} date={d.document_date}
      docMeta={{ organizationId: d.organization_id, documentType: 'doc.fixed_asset_sale', documentId: d.id, filename: `Продаж ОЗ ${d.document_number}` }}>
      <p>Покупець: <b>{buyer}</b>{d.agreement_text ? `. Підстава: ${d.agreement_text}` : ''}{d.comment ? `. ${d.comment}` : ''}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
        <thead><tr>{['№', 'Інв. №', 'Найменування', 'Первісна', 'Знос', 'Залишкова', 'Ціна без ПДВ', 'ПДВ', 'Разом з ПДВ'].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
        <tbody>
          {d.items.map((it) => {
            const a = names.get(it.fixed_asset_id);
            return (
              <tr key={it.id ?? it.line_number}>
                <td style={cell}>{it.line_number}</td><td style={cell}>{a?.inv ?? ''}</td><td style={cell}>{a?.name ?? '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.primary_cost_snapshot)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.accumulated_depreciation_snapshot)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.book_value_snapshot)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.sale_price_without_vat)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.vat_amount)}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.sale_price_with_vat)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr style={{ fontWeight: 'bold' }}><td colSpan={6} style={cell}>Разом</td>
          <td style={{ ...cell, textAlign: 'right' }}>{fmt2(sum((it) => Number(it.sale_price_without_vat)))}</td>
          <td style={{ ...cell, textAlign: 'right' }}>{fmt2(sum((it) => Number(it.vat_amount ?? 0)))}</td>
          <td style={{ ...cell, textAlign: 'right' }}>{fmt2(sum((it) => Number(it.sale_price_with_vat ?? 0)))}</td></tr></tfoot>
      </table>
      <Signatures left="Передав (продавець)" right="Прийняв (покупець)" />
    </Shell>
  );
}

// ===================== переміщення / модернізація / зміна стану =====================
import { internalTransfer as transferApi, modernization as modernizationApi } from '@/api/asset_movement';
import { assetStateChange as stateChangeApi, CHANGE_KIND_UA } from '@/api/assetStateChange';
import { departmentsApi, employees as employeesApi } from '@/api/hr';

export function InternalTransferPrintPage() {
  const { id } = useParams<{ id: string }>();
  usePrintMode();
  const docQ = useQuery({ queryKey: ['asset-transfer', id], queryFn: () => transferApi.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const names = useAssetNames(d?.organization_id);
  const depsQ = useQuery({ queryKey: ['departments', d?.organization_id], queryFn: () => departmentsApi.list({ organization_id: d!.organization_id, limit: 500 }), enabled: !!d });
  const empQ = useQuery({ queryKey: ['employees-all', d?.organization_id], queryFn: () => employeesApi.list({ organization_id: d!.organization_id, limit: 500 }), enabled: !!d });
  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const dep = (v?: string | null) => depsQ.data?.items.find((x) => x.id === v)?.name ?? '';
  const emp = (v?: string | null) => empQ.data?.items.find((x) => x.id === v)?.full_name ?? '';
  return (
    <Shell org={org} title="Акт внутрішнього переміщення основних засобів" number={d.document_number} date={d.document_date}
      docMeta={{ organizationId: d.organization_id, documentType: 'doc.asset_internal_transfer', documentId: d.id, filename: `Переміщення ОЗ ${d.document_number}` }}>
      <p>Підстава: <b>{d.transfer_reason}</b>{d.comment ? `. ${d.comment}` : ''}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
        <thead><tr>{['№', 'Інв. №', 'Найменування', 'З підрозділу', 'У підрозділ', 'Від МВО', 'До МВО'].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
        <tbody>
          {d.items.map((it) => {
            const a = names.get(it.fixed_asset_id ?? it.intangible_asset_id ?? it.low_value_asset_id ?? '');
            return (<tr key={it.id ?? it.line_number}><td style={cell}>{it.line_number}</td><td style={cell}>{a?.inv ?? ''}</td><td style={cell}>{a?.name ?? '—'}</td>
              <td style={cell}>{dep(it.from_department_id)}</td><td style={cell}>{dep(it.to_department_id)}</td><td style={cell}>{emp(it.from_employee_id)}</td><td style={cell}>{emp(it.to_employee_id)}</td></tr>);
          })}
        </tbody>
      </table>
      <Signatures left="Здав (МВО)" right="Прийняв (МВО)" />
    </Shell>
  );
}

export function ModernizationPrintPage() {
  const { id } = useParams<{ id: string }>();
  usePrintMode();
  const docQ = useQuery({ queryKey: ['fa-modernization', id], queryFn: () => modernizationApi.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const names = useAssetNames(d?.organization_id);
  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const a = names.get(d.fixed_asset_id);
  return (
    <Shell org={org} title="Акт модернізації (поліпшення) основного засобу" number={d.document_number} date={d.document_date}
      docMeta={{ organizationId: d.organization_id, documentType: 'doc.fixed_asset_modernization', documentId: d.id, filename: `Модернізація ${d.document_number}` }}>
      <p>Основний засіб: <b>{a ? `${a.inv} ${a.name}` : '—'}</b>. Первісна до: {fmt2(d.primary_cost_before)}, знос до: {fmt2(d.accumulated_depreciation_before)}.{d.comment ? ` ${d.comment}` : ''}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5pt' }}>
        <thead><tr>{['№', 'Витрати', 'Рахунок', 'Сума'].map((h) => <th key={h} style={head}>{h}</th>)}</tr></thead>
        <tbody>{d.items.map((it) => <tr key={it.id ?? it.line_number}><td style={cell}>{it.line_number}</td><td style={cell}>{it.description}</td><td style={cell}>{it.cost_account_code}</td><td style={{ ...cell, textAlign: 'right' }}>{fmt2(it.amount)}</td></tr>)}</tbody>
        <tfoot><tr style={{ fontWeight: 'bold' }}><td colSpan={3} style={cell}>Разом капіталізовано</td><td style={{ ...cell, textAlign: 'right' }}>{fmt2(d.total_capitalized)}</td></tr></tfoot>
      </table>
      <Signatures left="Голова комісії" right="МВО" />
    </Shell>
  );
}

const STATUS_UA2: Record<string, string> = { PARKED: 'на зберіганні', OPERATING: 'в експлуатації', CONSERVATION: 'на консервації', REPAIR_LONG: 'у тривалому ремонті' };

export function StateChangePrintPage() {
  const { id } = useParams<{ id: string }>();
  usePrintMode();
  const docQ = useQuery({ queryKey: ['asset-state-change', id], queryFn: () => stateChangeApi.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const names = useAssetNames(d?.organization_id);
  if (!d || !org) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const a = names.get(d.fixed_asset_id ?? d.intangible_asset_id ?? '');
  const isLife = d.change_kind === 'USEFUL_LIFE';
  return (
    <Shell org={org} title={isLife ? 'Акт зміни строку корисного використання' : `Акт зміни стану: ${CHANGE_KIND_UA[d.change_kind]}`} number={d.document_number} date={d.document_date}
      docMeta={{ organizationId: d.organization_id, documentType: 'doc.asset_state_change', documentId: d.id, filename: `Зміна стану ${d.document_number}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}><tbody>
        <tr><td style={{ ...cell, width: '35%', fontWeight: 600 }}>Актив</td><td style={cell}>{a ? `${a.inv} ${a.name}` : '—'}</td></tr>
        <tr><td style={{ ...cell, fontWeight: 600 }}>Операція</td><td style={cell}>{CHANGE_KIND_UA[d.change_kind]}</td></tr>
        <tr><td style={{ ...cell, fontWeight: 600 }}>Дата зміни</td><td style={cell}>{fmtD(d.effective_date)}</td></tr>
        {isLife
          ? <tr><td style={{ ...cell, fontWeight: 600 }}>Строк корисного використання</td><td style={cell}>{d.prev_useful_life_months ?? '…'} → {d.new_useful_life_months} міс</td></tr>
          : <tr><td style={{ ...cell, fontWeight: 600 }}>Стан</td><td style={cell}>{STATUS_UA2[d.prev_status ?? ''] ?? d.prev_status ?? '…'} → {STATUS_UA2[d.change_kind] ?? d.change_kind}</td></tr>}
        <tr><td style={{ ...cell, fontWeight: 600 }}>Підстава</td><td style={cell}>{d.reason ?? ''}</td></tr>
        {d.comment && <tr><td style={{ ...cell, fontWeight: 600 }}>Коментар</td><td style={cell}>{d.comment}</td></tr>}
      </tbody></table>
      <Signatures left="Керівник" right="Головний бухгалтер" />
    </Shell>
  );
}
